import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronRight, Mail, Settings2, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { ChatPageAccess, ChatPageAccessDto, PublicUrlDto } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath } from '@/Config/app/router/index.path';
import { pushGtmEvent } from '@/Config/libs/analytics';
import type { Integrations } from '@/Domain/components/KnowledgeBaseArticles/types';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { CollapsibleCard } from '@/Domain/components/Shared/CollapsibleCard/CollapsibleCard';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/Domain/components/Shared/Item/Item';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  RadioGroupSegmented,
  RadioGroupSegmentedItem,
} from '@/Domain/components/Shared/RadioGroup/RadioGroup';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import { GtmEventEnum } from '@/Domain/enum/GtmEvent.enum';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { useAutoSubmit } from '@/Domain/hooks/App/useAutoSave.hook';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useSupportWidgetStore } from '@/Domain/store/supportWidget.store';
import { CopyField } from '@/UI/Website/Install/CopyField';
import { EmailToDeveloperDialog } from '@/UI/Website/Install/EmailToDeveloperDialog';
import {
  INSTALL_METHODS,
  InstallMethodKey,
  InstallMethodSheet,
} from '@/UI/Website/Install/InstallMethodSheet';
import { INSTALL_METHOD_QUERY_PARAM } from '@/UI/Website/Install/installSnippets';
import { ImplementationData } from '@/UI/Website/WidgetSettings.page';

type SnippetType = 'widget' | 'embed' | 'widget_shopify';

const sectionHeading = 'mb-3 block text-sm font-semibold text-foreground';

const previewUrl = (path: string | undefined): string | undefined =>
  path ? window.location.origin + path : undefined;

export const Install = () => {
  const { t } = useTranslation(['widget', 'common', 'website']);
  const { capture } = usePostHogAnalytics();
  const openWithMessage = useSupportWidgetStore((state) => state.openWithMessage);

  const [openMethod, setOpenMethod] = useState<InstallMethodKey | null>(null);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: implementation } = useQuery<ImplementationData>({ queryKey: ['implementation'] });
  const { data: integrations } = useQuery<Integrations>({ queryKey: ['integrations'] });
  const { data: shopifyEditorLink } = useQuery<{ editor_url: string | null }>({
    queryKey: [QueryKey.shopify.editorLink],
  });

  const form = useForm<PublicUrlDto>({
    defaultValues: { public_url_active: implementation?.public_url_active ?? false },
  });

  const mutation = useMutation<PublicUrlDto, Error, PublicUrlDto>({
    mutationKey: [QueryKey.integration.publicUrl],
  });

  useAutoSubmit<PublicUrlDto>(form, (data) => mutation.mutate(data as PublicUrlDto), false);

  const { data: chatPageAccessData } = useQuery<ChatPageAccessDto>({
    queryKey: [QueryKey.chatPageAccess.get],
  });
  const chatPageAccessMutation = useMutation<ChatPageAccessDto, Error, ChatPageAccess>({
    mutationKey: [QueryKey.chatPageAccess.patch],
    onError: () => toast.error(t('website:chat_page.error')),
  });

  const scenarioId = implementation?.scenario_id ?? '';
  const hasShopifyMcp = implementation?.has_shopify_mcp_action ?? false;

  const handleSnippetCopy = (snippetType: SnippetType) => {
    capture(PostHogEventEnum.WidgetSnippetCopied, { snippet_type: snippetType });
    pushGtmEvent(GtmEventEnum.WidgetSnippetCopied, { snippet_type: snippetType });
  };

  const handleAgentIdCopy = () => capture(PostHogEventEnum.InstallAgentIdCopied);

  const openMethodSheet = (method: InstallMethodKey) => {
    setOpenMethod(method);
    capture(PostHogEventEnum.InstallMethodOpened, { method });
  };

  // Deep-link: `/website?tab=install&install_method=<key>` auto-opens that method's sheet
  // (used by the Shopify post-onboarding checklist), then drops the param.
  useEffect(() => {
    const method = searchParams.get(INSTALL_METHOD_QUERY_PARAM);
    if (!method || !INSTALL_METHODS.some((m) => m.key === method)) return;
    setOpenMethod(method as InstallMethodKey);
    capture(PostHogEventEnum.InstallMethodOpened, { method });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(INSTALL_METHOD_QUERY_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams, capture]);

  const handleAskAi = () => {
    capture(PostHogEventEnum.InstallAskAiClicked);
    openWithMessage(t('website:install.ask_ai_message'));
  };

  const handleEmailDeveloper = () => setEmailDialogOpen(true);

  return (
    <div className="relative flex flex-col gap-7">
      <div>
        <Label className={sectionHeading}>{t('website:install.heading')}</Label>
        <div className="flex flex-col gap-2.5">
          {INSTALL_METHODS.map(({ key, Icon, color }) => (
            <Item
              key={key}
              variant="outline"
              size="sm"
              className="cursor-pointer hover:bg-accent"
              role="button"
              tabIndex={0}
              data-testid={`install-method-${key}`}
              onClick={() => openMethodSheet(key)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openMethodSheet(key);
                }
              }}
            >
              <ItemMedia variant="icon" className="bg-muted" style={{ color }}>
                <Icon className="size-4" />
              </ItemMedia>
              <ItemContent>
                <ItemTitle>{t(`website:install.methods.${key}.title`)}</ItemTitle>
                <ItemDescription className="text-xs">
                  {t(`website:install.methods.${key}.desc`)}
                </ItemDescription>
              </ItemContent>
              <ItemActions>
                <ChevronRight className="size-4 text-foreground-subtle" />
              </ItemActions>
            </Item>
          ))}
        </div>
      </div>

      <div>
        <Label className={sectionHeading}>{t('website:install.help.heading')}</Label>
        <div className="flex flex-col gap-2.5">
          <Button
            variant="secondary"
            size="md"
            className="justify-center"
            onClick={handleAskAi}
            data-testid="install-ask-ai"
          >
            <Sparkles className="size-4" />
            {t('website:install.help.ask_ai')}
          </Button>
          <Button
            variant="secondary"
            size="md"
            className="justify-center"
            onClick={handleEmailDeveloper}
            data-testid="install-email-dev"
          >
            <Mail className="size-4" />
            {t('website:install.help.email_dev')}
          </Button>
        </div>
      </div>

      <CollapsibleCard
        icon={<Settings2 className="size-4 text-muted-foreground" />}
        title={t('website:install.availability.title')}
        description={t('website:install.availability.description')}
        open={availabilityOpen}
        onToggle={() => setAvailabilityOpen((value) => !value)}
        testId="install-availability-card"
        headerTestId="install-availability-trigger"
      >
        <div className="space-y-5">
          <Item variant="muted" className="w-full" size="sm">
            <ItemContent>
              <ItemTitle className="line-clamp-1">
                {t('website:widget_embed_access.title')}
              </ItemTitle>
              <ItemDescription>{t('website:widget_embed_access.description')}</ItemDescription>
            </ItemContent>
            <ItemActions className="shrink-0">
              <Controller
                name="public_url_active"
                control={form.control}
                render={({ field }) => (
                  <Switch id={field.name} checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
            </ItemActions>
          </Item>

          <div className="space-y-1.5" data-testid="chat-page-access-section">
            <Label data-testid="chat-page-access-title">{t('website:chat_page.title')}</Label>
            <p className="text-sm text-muted-foreground" data-testid="chat-page-access-description">
              {t('website:chat_page.description')}{' '}
              <Link
                to={`../${IndexPath.user.accountSettings}`}
                viewTransition
                className="text-foreground !underline !decoration-foreground underline-offset-2 hover:opacity-70"
                data-testid="invite-chat-users-link"
              >
                {t('website:chat_page.invite_link')}
              </Link>{' '}
              {t('website:chat_page.invite_suffix')}
            </p>
            <RadioGroupSegmented
              value={chatPageAccessData?.chat_page_access ?? 'public'}
              onValueChange={(value) => chatPageAccessMutation.mutate(value as ChatPageAccess)}
              data-testid="chat-page-access-select"
            >
              {(['public', 'restricted', 'disabled'] as const).map((value) => (
                <RadioGroupSegmentedItem key={value} value={value} id={`access-${value}`}>
                  {t(`website:chat_page.access.${value}`)}
                </RadioGroupSegmentedItem>
              ))}
            </RadioGroupSegmented>
          </div>
        </div>
      </CollapsibleCard>

      {scenarioId && (
        <div className="space-y-1.5">
          <CopyField
            label={t('website:install.agent_id.label')}
            value={scenarioId}
            testid="install-agent-id"
            copyTestid="install-agent-id-copy"
            onCopy={handleAgentIdCopy}
          />
          <p className="text-xs text-foreground-subtle">{t('website:install.agent_id.help')}</p>
        </div>
      )}

      <InstallMethodSheet
        method={openMethod}
        onOpenChange={(open) => !open && setOpenMethod(null)}
        scenarioId={scenarioId}
        hasShopifyMcp={hasShopifyMcp}
        chatPageUrl={previewUrl(integrations?.links.public)}
        widgetPreviewUrl={previewUrl(integrations?.links.widget)}
        embedPreviewUrl={previewUrl(integrations?.links.embed)}
        shopifyEditorUrl={shopifyEditorLink?.editor_url}
        onAgentIdCopy={handleAgentIdCopy}
        onSnippetCopy={handleSnippetCopy}
      />

      <EmailToDeveloperDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        scenarioId={scenarioId}
        onSent={() => capture(PostHogEventEnum.InstallInstructionsEmailed)}
      />
    </div>
  );
};
